/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  useGetAuditLogCountsLazyQuery,
  AuditLogSearchFiltersInput,
} from "~/generated/graphql";
import { showError } from "~/services/toast";

export type DailyCount = {
  date: Date;
  counts: [number, number, number, number];
};

type Props = {
  timeRangeStart: string;
  timeRangeEnd: string;
  filters?: AuditLogSearchFiltersInput;
};

type AuditLogItem = {
  datetime: string;
};

/**
 * Hook to get audit log counts with filter support by querying the auditLogs API.
 * Since the API only supports 1-day queries, this hook splits the week into 7 daily queries,
 * fetches all pages for each day, and aggregates the results into 6-hour time buckets.
 */
export function useGetAuditLogCounts({
  timeRangeStart,
  timeRangeEnd,
  filters,
}: Props) {
  const [data, setData] = useState<DailyCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [fetchAuditLogs] = useGetAuditLogCountsLazyQuery({
    fetchPolicy: "network-only",
    onError: (err) => {
      showError(err.message);
      setError(err);
    },
  });

  // Serialize filters to prevent infinite re-renders
  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  useEffect(() => {
    // Set loading immediately when dependencies change and clear old data
    setLoading(true);
    setError(null);
    setData([]); // Clear old data to show loading state

    const fetchWeekData = async () => {
      try {
        // Split the week into daily ranges
        const dailyRanges = getDailyRanges(timeRangeStart, timeRangeEnd);

        // Fetch all days in parallel
        const dailyResults = await Promise.all(
          dailyRanges.map((range) =>
            fetchAllPagesForDay(fetchAuditLogs, range, filters)
          )
        );

        // Aggregate results into daily counts
        const counts = aggregateDailyCounts(dailyResults);

        setData(counts);
      } catch (err) {
        const fetchError = err instanceof Error ? err : new Error(String(err));
        setError(fetchError);
        showError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeekData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRangeStart, timeRangeEnd, filtersKey]);

  return {
    data,
    loading,
    error,
  };
}

/**
 * Split the week range into individual daily ranges
 */
function getDailyRanges(
  start: string,
  end: string
): Array<{ start: string; end: string; date: Date }> {
  const startDate = dayjs(start).startOf("day");
  const endDate = dayjs(end).startOf("day");

  // Calculate number of days in the range (inclusive)
  const dayCount = endDate.diff(startDate, "day") + 1;

  // Generate array of day indices and map to daily ranges
  return Array.from({ length: dayCount }, (_, index) => {
    const currentDate = startDate.add(index, "day");
    return {
      start: currentDate.startOf("day").toISOString(),
      end: currentDate.endOf("day").toISOString(),
      date: currentDate.toDate(),
    };
  });
}

/**
 * Fetch all pages for a single day using parallel requests
 */
async function fetchAllPagesForDay(
  fetchAuditLogs: ReturnType<typeof useGetAuditLogCountsLazyQuery>[0],
  range: { start: string; end: string; date: Date },
  filters?: AuditLogSearchFiltersInput
): Promise<{ date: Date; items: AuditLogItem[] }> {
  const limit = 100;

  // Fetch first page to get total page count
  const firstPageResult = await fetchAuditLogs({
    variables: {
      input: {
        page: 1,
        limit,
        timeRangeStart: range.start,
        timeRangeEnd: range.end,
        filters,
      },
    },
  });

  const firstPageItems = firstPageResult.data?.auditLogs?.results ?? [];
  const totalPages = firstPageResult.data?.auditLogs?.pages ?? 1;

  // If only one page, return immediately
  if (totalPages <= 1) {
    return {
      date: range.date,
      items: firstPageItems,
    };
  }

  // Fetch remaining pages in parallel
  const remainingPagePromises = Array.from(
    { length: totalPages - 1 },
    (_, index) =>
      fetchAuditLogs({
        variables: {
          input: {
            page: index + 2,
            limit,
            timeRangeStart: range.start,
            timeRangeEnd: range.end,
            filters,
          },
        },
      })
  );

  const remainingResults = await Promise.all(remainingPagePromises);

  // Combine all items
  const allItems = [
    ...firstPageItems,
    ...remainingResults.flatMap(
      (result) => result.data?.auditLogs?.results ?? []
    ),
  ];

  return {
    date: range.date,
    items: allItems,
  };
}

/**
 * Aggregate audit log items into daily counts with 6-hour time buckets
 */
function aggregateDailyCounts(
  dailyResults: Array<{ date: Date; items: AuditLogItem[] }>
): DailyCount[] {
  return dailyResults.map(({ date, items }) => {
    const counts = items.reduce(
      (acc, item) => {
        const bucketIndex = getTimeBucketIndex(item.datetime);
        if (bucketIndex !== null) {
          return [
            bucketIndex === 0 ? acc[0] + 1 : acc[0],
            bucketIndex === 1 ? acc[1] + 1 : acc[1],
            bucketIndex === 2 ? acc[2] + 1 : acc[2],
            bucketIndex === 3 ? acc[3] + 1 : acc[3],
          ] as [number, number, number, number];
        }
        return acc;
      },
      [0, 0, 0, 0] as [number, number, number, number]
    );

    return {
      date,
      counts,
    };
  });
}

/**
 * Determine which 6-hour time bucket an audit log belongs to
 * Bucket 0: 00:00-05:59
 * Bucket 1: 06:00-11:59
 * Bucket 2: 12:00-17:59
 * Bucket 3: 18:00-23:59
 */
function getTimeBucketIndex(datetime: string): number | null {
  const date = dayjs(datetime);

  if (!date.isValid()) {
    return null;
  }

  const hour = date.hour();
  return Math.floor(hour / 6) as 0 | 1 | 2 | 3;
}
